/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, TimeService, UNPLACEHOLDER } from '@dm/core';
import { SWITCH } from 'app/config';

@Injectable()
export class TaskBatchStrService {
    get now(): Date {
        // 获取设备时间
        return new Date(this.time.currTime * 1e3);
    }

    // 任务名称配置
    taskNameText = {
        createSmartcachePool: this.i18n.get('storage.smartCache_task_createPool_title'),
        createStoragePool: this.i18n.get('monitor.taskCenter_createStoragePool_taskName_value'),
        deleteStoragePool: this.i18n.get('monitor.taskCenter_deleteStoragePool_taskName_value'),
        expandStoragePool: this.i18n.get('monitor.taskCenter_expandStoragePool_taskName_value'),
        modifyStoragePool: this.i18n.get('monitor.taskCenter_modifyStoragePool_taskName_value'),
        createlun: this.i18n.get('monitor.taskCenter_view_createA_lun_taskName_omtask_value'),
        expandLun: this.i18n.get('monitor.taskCenter_expandLUN_taskName_value'),
        createLunGroup: this.i18n.get('monitor.taskCenter_view_createA_lunGroup_taskName_value'),
        createlungrouptask: this.i18n.get('monitor.taskCenter_createWithMapping_value'),
        startProtectionForPG: this.i18n.get('monitor.taskCenter_enableProtection_forPG_omtask_value'),
        startProtectionLun: this.i18n.get('monitor.taskCenter_startProtection_withLUN_value'),
        addluntogroup: this.i18n.get('monitor.taskCenter_addLun_toPG_omtask_value'),
        addLuntoLG: this.i18n.get('monitor.taskCenter_addLun_toPG_omtask_value'),
        removeLunFromGroup: this.i18n.get('monitor.taskCenter_removeLun_fromPG_omtask_value'),
        addLuntoLunGroup: this.i18n.get('monitor.taskCenter_addLun_to_LunGroup_value'),
        removeLunFromLunGroup: this.i18n.get('monitor.taskCenter_removeLun_fromPG_omtask_value'),
        splitProtectGroup: this.i18n.get('monitor.taskCenter_splitProtection_task_taskName_value'),
        snapGroupMapping: this.i18n.get('monitor.taskCenter_mapSnapshotCG_taskName_value'),
        cloneGroupMapping: this.i18n.get('monitor.taskCenter_mapCloneCG_taskName_value'),
        syncCloneGroup: this.i18n.get('monitor.taskCenter_startSyncCloneCG_taskName_value'),
        restoreCloneGroup: this.i18n.get('monitor.taskCenter_startRevSyncCloneCG_taskName_value'),
        createCloneCG: this.i18n.get('monitor.taskCenter_createCloneCG_taskName_value'),
        addPG2Schedule: this.i18n.get('storage.lun_task_addtoHyperCDPSchedule_button'),
        createProtectGroup: this.i18n.get('protection.protectionGroup_create_title'),
        startProtectionFileSystem: this.i18n.get('monitor.taskCenter_enableProtection_forFS_value'),
        activeSnapshotCg: this.i18n.get('protection.snapshotCG_task_active_tittle'),
        rollbackSnapshotCg: this.i18n.get('protection.snapshotCG_term_startRollback_title'),
        reconstructSnapshotCg: this.i18n.get('protection.snapshotCG_task_reactive_tittle'),
        splitSmartMigration: this.i18n.get('storage.smartMigration_task_split_title'),
        consistencySplitSmartMigration: this.i18n.get('storage.smartMigration_task_consistentSplit_omtask_title'),
        // 文件系统
        createFileSystem: this.i18n.get('service.fileSystem_term_create_title'),
        // 文件共享
        createFileShare: this.i18n.get('service.share_simplifiedMode_create_wintitle'),
        // 双活租户pair
        createVstorePair: this.i18n.get('storage.hyperMetro_task_create_vstorePair_omtask_title'),
        // 远程复制租户pair
        createRemoteRepVstorePair: this.i18n.get('storage.hyperMetro_task_create_vstorePair_omtask_title'),
        // 容器
        startContainer: this.i18n.get('settings.container_task_enable_title'),
        stopContainer: this.i18n.get('settings.container_task_disable_title'),
        expandNodes: this.i18n.get('rest.addNodes_omtask_msg'),
        deployContainer: this.i18n.get('rest.deployContainer_msg'),
        activateContainer: this.i18n.get('rest.activeContainer_msg'),
        deployBackupApp: this.i18n.get('rest.oneClickInstallOPAppliance_msg'),
        // nas
        activateFileService: this.i18n.get('rest.activeFileService_omtask_msg'),
        addClient2CifsShare: this.i18n.get('service.share_term_createCIFS_addPermission_title'),
        addClient2NfsShare: this.i18n.get('service.share_term_createNFS_addPermission_title'),
        createNfsShare: this.i18n.get('service.share_term_createNFS_title'),
        createCifsShare: this.i18n.get('service.share_term_createCIFS_title'),
        // 创建双活pair
        createHyperMetroPair: this.i18n.get('protection.hyperMetro_task_createPair_omtask_title'),
        createDataTurboShare: this.i18n.get('service.share_term_createDataTurbo_title'),
        addDataTurboClientShare: this.i18n.get('service.share_term_createDataTurbo_addPermission_msg'),

        // 切换文件系统双活域业务工作站点(AA模式)
        switchHMWorkSiteInAA: this.i18n.get('protection.HyperMetroDomain_task_switchServiceSite_msg'),

        // 切换文件系统双活域工作模式(AA->AP,AP->AA)
        switchHMWorkMode: this.i18n.get('protection.HyperMetroDomain_task_switchWordMode_msg'),

        // 切换文件系统双活域AP站点(AP模式)
        switchHMSiteInAP: this.i18n.get('protection.HyperMetroDomain_task_switchAPSite_msg'),
        // 迁移租户pair
        createSmartMoveVstorePair: this.i18n.get('storage.hyperMetro_task_create_vstorePair_omtask_title'),
        // 创建SmartMove文件系统 Pair
        createMoveFsPair: this.i18n.get('storage.smartMove_task_fsPair_create_title'),
    };

    i18nLabel = {
        numberCol: this.i18n.get('common.Number_col'),
        object: this.i18n.get('monitor.task_target_label'),
        taskStatus: this.i18n.get('common.status_column'),
        startTime: this.i18n.get('common.startTime_label'),
        endTime: this.i18n.get('common.endTime_label'),
        duration: this.i18n.get('common.duration_label'),
        stepName: this.i18n.get('common.name_column'),
        stepStatus: this.i18n.get('common.status_column'),
        closeBtn: this.i18n.get('common.close_button'),
        progressLab: this.i18n.get('system.upgrade_term_progress_lab'),
        Resulttitle: this.i18n.get('common.executeResult_title'),
        executeTimes: this.i18n.get('common.result_executeTimes_col'),
        resultBackgroudExecuteinfo: this.i18n.get(
            SWITCH.IS_SIMPLE_MODE ? 'common.result_simplifiedMode_backgroudExecute_info' : 'common.result_backgroudExecute_info'),
        taskSucceed_tip: this.i18n.get('common.taskCenter_enableProtection_forFS_taskSucceed_tip'),
        taskFailed: this.i18n.get('common.taskCenter_enableProtection_forFS_taskFailed_tip'),
    };

    statusText = {
        wait: this.i18n.get('monitor.task_status_result_waiting'),
        executing: this.i18n.get('monitor.task_status_result_running'),
        success: this.i18n.get('common.downloadsuccess_label'),
        fail: this.i18n.get('common.downloadFailed_label'),
        rollback_success: this.i18n.get('monitor.task_status_result_rollbackSuccess'),
        rollback_fail: this.i18n.get('monitor.task_status_result_rollbackFail'),
        rollback: this.i18n.get('monitor.task_status_result_rollbacking'),
        partial_fail: this.i18n.get('monitor.task_status_result_partSucceed'),
    };

    color = {
        success: '#3DCCA6',
        failed: '#ff4c4c',
        running: '#3DCCA6',
        partial: '#FF9A52',
    };

    simpleModeColor = {
        success: '#53ab6b',
        failed: '#c63939',
        running: '#53ab6b',
        partial: '#e6e6e6',
    };

    statusColor = {
        wait: this.color.running,
        executing: this.color.running,
        success: this.color.success,
        fail: this.color.failed,
        rollback_success: this.color.success,
        rollback_fail: this.color.failed,
        rollback: this.color.running,
        partial_fail: this.color.partial,
    };

    simpleModeStatusColor = {
        wait: this.simpleModeColor.running,
        executing: this.simpleModeColor.running,
        success: this.simpleModeColor.success,
        fail: this.simpleModeColor.failed,
        rollback_success: this.simpleModeColor.success,
        rollback_fail: this.simpleModeColor.failed,
        rollback: this.simpleModeColor.running,
        partial_fail: this.simpleModeColor.partial,
    };

    icon = {
        wait: '#icon-loading',
        executing: '#icon-loading',
        success: '#icon-status-succeed',
        fail: '#icon-status-failed',
        rollback_success: '#icon-status-succeed',
        rollback_fail: '#icon-status-failed',
        rollback: '#icon-loading',
        partial_fail: '',
    };

    simpleModeIcon = {
        wait: '#icon-loading',
        executing: '#icon-loading',
        success: '#core-icon-simple-status-succeed',
        fail: '#core-icon-simple-status-failed',
        rollback_success: '#core-icon-simple-status-succeed',
        rollback_fail: '#core-icon-simple-status-failed',
        rollback: '#icon-loading',
        partial_fail: '',
    };

    secStr = this.i18n.get('common.second_short_label');
    minStr = this.i18n.get('common.min_short_label');
    hourStr = this.i18n.get('common.hour_short_label');

    taskStatusOptions: { [key: string]: string | number };

    constructor(
        private time: TimeService,
        private i18n: I18nService,
    ) {
        this.taskStatusOptions = this.statusText;
    }

    // 生成子步骤失败的国际化标题
    stepFailedTitle(name): string {
        return this.i18n.get('common.rest_fail_label', [name]);
    }

    preCheckStepFailedTitle(name): string {
        return this.i18n.get('common.rest_reason_label', [name]);
    }

    // 转换UTC时间戳为带时区信息的字符串
    renderTime(time: number): string {
        return time === 0 ?
            UNPLACEHOLDER :
            this.time.formatUTC(time * 1e3);
    }

    // 渲染持续时间
    renderDuration(duration: number): string {
        // 如果持续时间为NaN或者负数，返回'--'
        if (isNaN(duration) || duration < 0) {
            return UNPLACEHOLDER;
        }

        const isZH = this.i18n.language === 'zh';

        const hourStr = isZH ? this.hourStr : ` ${this.hourStr} `;
        const minStr = isZH ? this.minStr : ` ${this.minStr} `;
        const secStr = isZH ? this.secStr : `${this.secStr}`;

        // 如果持续时间为0，返回'小于1秒'
        if (duration === 0) {
            return `< 1${secStr}`;
        }

        const sec = duration % 60;
        const min = Math.floor(duration / 60) % 60;
        const hour = Math.floor(duration / 3600);

        let result = '';
        result += hour ? hour + hourStr : '';
        result += min ? min + minStr : '';
        result += sec || !result ? sec + secStr : '';

        return result.trim();
    }

    // 计算持续时间
    getDuration(startTime, endTime): number {
        const now = Math.floor(this.now.getTime() / 1000);
        const startTimeResult = Number(startTime) ? Number(endTime) - startTime : NaN;
        const endTimeResult = Number(startTime) ? now - startTime : NaN;
        const duration = Number(endTime) ? startTimeResult : endTimeResult;

        return duration;
    }
}
